import type {ReactElement} from 'react';
import React from 'react';
import {convertSecondsToMMSSFormat} from '@Utils/date.util';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ControlledList} from '@Components/controlled-list';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {CHANGE_DURATION_LIST_ID, SelectorType} from '@Components/poster-editor/components/poster-seekbar/components/change-duration-selector/change-duration-selector.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {isVideoBackground} from '@PosterWhiteboard/page/background/background.types';
import styles from './change-duration-selector.module.scss';
import {useGetPageMinDuration} from '@Hooks/poster-editor/useGetPageMinDuration';

const DURATION_OPTION_ID_PREFIX = 'durationOption';
interface ChangeDurationSelectorProps {
  selectedType?: SelectorType;
  onSelectType(id: SelectorType): void;
}

export function ChangeDurationSelector({...props}: ChangeDurationSelectorProps): ReactElement<any> {
  const minDuration = useGetPageMinDuration();
  const getImageThumbnail = (src: string, isSelected: boolean): ReactElement<any> => {
    return (
      <div className={`flex-center _full-height _full-width radius-8 ${isSelected ? styles.selectedIconThumbnailBg : styles.iconThumbnailBg}`}>
        <img className={`${styles.img} _full-width _full-height`} alt="" src={src} />
      </div>
    );
  };

  const getIconThumbnail = (icon: string, isSelected: boolean): ReactElement<any> => {
    return (
      <div className={`flex-center _full-height _full-width radius-8 ${isSelected ? styles.selectedIconThumbnailBg : styles.iconThumbnailBg}`}>
        <Icon icon={icon} size={IconSize.SIZE_ICON_24} isSelected={isSelected} />
      </div>
    );
  };

  const getSlideshowThumbnail = (isSelected: boolean): ReactElement<any> => {
    const item = window.posterEditor?.whiteboard?.getCurrentPage().items.getLongestStreamingSlideshowItem();

    if (!item) {
      return getIconThumbnail('icon-slideshow', isSelected);
    }

    return getImageThumbnail(item.fabricObject.toDataURL(), isSelected);
  };

  const getVideoThumbnail = (isSelected: boolean): ReactElement<any> => {
    const item = window.posterEditor?.whiteboard?.getCurrentPage().items.getLongestVideoItem();

    if (!item) {
      return getIconThumbnail('icon-video', isSelected);
    }

    return getImageThumbnail(item.fabricObject.toDataURL(), isSelected);
  };

  const getVideoBackgroundThumbnail = (isSelected: boolean): ReactElement<any> => {
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    if (page && isVideoBackground(page.background.details)) {
      return getImageThumbnail(page.background.details.fabricObject.toDataURL(), isSelected);
    }
    return getIconThumbnail('icon-videocam', isSelected);
  };

  const getVideoBackgroundDuration = (): number => {
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    if (page && isVideoBackground(page.background.details)) {
      return page.background.details.getDuration();
    }
    return 0;
  };

  const getListItems = (): ControlledListItemType[] => {
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    const dropdownList: ControlledListItemType[] = [];
    if (page?.items.hasVideoItem() && minDuration < page?.items.getLongestVideoItemDuration()) {
      dropdownList.push({
        id: `${DURATION_OPTION_ID_PREFIX}${SelectorType.VIDEO}`,
        text: window.i18next.t('pmwjs_video'),
        subText: convertSecondsToMMSSFormat(Math.round(page?.items.getLongestVideoItemDuration())),
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: getVideoThumbnail(SelectorType.VIDEO === props.selectedType),
        },
        onClick: onClickHandler,
        isSelected: SelectorType.VIDEO === props.selectedType,
      });
    }

    const videoBackgroundDuration = getVideoBackgroundDuration();
    if (page?.background.hasVideoBackground() && minDuration < videoBackgroundDuration) {
      dropdownList.push({
        id: `${DURATION_OPTION_ID_PREFIX}${SelectorType.VIDEO_BACKGROUND}`,
        text: window.i18next.t('pmwjs_background_video'),
        subText: convertSecondsToMMSSFormat(Math.round(videoBackgroundDuration)),
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: getVideoBackgroundThumbnail(SelectorType.VIDEO_BACKGROUND === props.selectedType),
        },
        onClick: onClickHandler,
        isSelected: SelectorType.VIDEO_BACKGROUND === props.selectedType,
      });
    }

    if (page?.items.hasStreamingSlideShowItem() && minDuration < page?.items.getLongestSlideshowItemDuration()) {
      dropdownList.push({
        id: `${DURATION_OPTION_ID_PREFIX}${SelectorType.SLIDESHOW}`,
        text: window.i18next.t('pmwjs_slideshow'),
        subText: convertSecondsToMMSSFormat(Math.round(page?.items.getLongestSlideshowItemDuration())),
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: getSlideshowThumbnail(SelectorType.SLIDESHOW === props.selectedType),
        },
        onClick: onClickHandler,
        isSelected: SelectorType.SLIDESHOW === props.selectedType,
      });
    }

    if (window.posterEditor?.whiteboard?.getCurrentPage().audioClips.hasAudio() && minDuration < window.posterEditor?.whiteboard?.getCurrentPage().audioClips.getDuration()) {
      dropdownList.push({
        id: `${DURATION_OPTION_ID_PREFIX}${SelectorType.AUDIO}`,
        text: window.i18next.t('pmwjs_audio'),
        subText: convertSecondsToMMSSFormat(Math.round(window.posterEditor?.whiteboard?.getCurrentPage().audioClips.getDuration())),
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: getIconThumbnail('icon-audio', SelectorType.AUDIO === props.selectedType),
        },
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        onClick: onClickHandler,
        isSelected: SelectorType.AUDIO === props.selectedType,
      });
    }

    return dropdownList;
  };

  const onClickHandler = (itemId: string): void => {
    props.onSelectType(itemId.replace(DURATION_OPTION_ID_PREFIX, '') as SelectorType);
  };

  return <ControlledList className="spacing-m-0 spacing-m-b-3" id={CHANGE_DURATION_LIST_ID} items={getListItems()} />;
}
